import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { UpdatePasswordForm } from '../features';
import { ConfirmationDialog } from '../shared/components';
import { type PasswordConfig } from '../shared/types';
import { urls } from '../shared/utils';

interface PWData {
  oneTimeToken?: string;
  passwordValidationConfig: PasswordConfig;
}

function decodeData(data: string | null): null | PWData {
  if (!data) {
    return null;
  }

  try {
    const decoded = atob(data);
    const parsed = JSON.parse(decoded) as Partial<PWData>;

    if (parsed.oneTimeToken) {
      return {
        oneTimeToken: parsed.oneTimeToken,
        passwordValidationConfig: parsed.passwordValidationConfig ?? {},
      };
    }

    return null;
  } catch (e) {
    return null;
  }
}

/**
 * Password Update Page
 * @returns JSX.Element
 */
function UpdatePasswordPage(): JSX.Element | null {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const data = params.get('data');
  const config = decodeData(data);

  const [isSuccess, setIsSuccess] = React.useState(false);

  const history = useHistory();

  useEffect(() => {
    if (!config?.oneTimeToken) {
      history.push(urls.Login);
    }
  }, [config, history]);

  if (!config?.oneTimeToken) {
    return null;
  }

  if (isSuccess) {
    return (
      <ConfirmationDialog
        title='Your password has been updated!'
        subtitle='Login with your new password.'
        continueLabel={(countdown) => `Go to Login (${countdown}s)`}
        onContinue={() => history.push(`${urls.Login}`)}
      />
    );
  }

  const { oneTimeToken, passwordValidationConfig: passwordConfig } = config;

  return (
    <UpdatePasswordForm
      oneTimeToken={oneTimeToken}
      passwordConfig={passwordConfig}
      onSuccess={() => setIsSuccess(true)}
    />
  );
}

export const Component = UpdatePasswordPage;
export const title = 'Update Password';
export const path = urls[title];
